import { useCallback, useEffect, useMemo, useState } from "react";
import { addMonths, addWeeks, format, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from "date-fns";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import CalendarMonth from "@/components/CalendarMonth";
import CalendarWeek from "@/components/CalendarWeek";
import CalendarList from "@/components/CalendarList";
import ActivityDrawer from "@/components/ActivityDrawer";
import CreateActivityDialog from "@/components/CreateActivityDialog";
import EventImportDialog from "@/components/EventImportDialog";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Plus, Upload } from "lucide-react";

const VIEWS = ["month", "week", "list"];

export default function CalendarPage() {
    const [view, setView] = useState("month");
    const [currentDate, setCurrentDate] = useState(new Date());
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);

    const [drawerId, setDrawerId] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [importOpen, setImportOpen] = useState(false);
    const [editActivity, setEditActivity] = useState(null);
    const [initialDate, setInitialDate] = useState(null);

    const range = useMemo(() => {
        if (view === "week") {
            return {
                start: startOfWeek(currentDate, { weekStartsOn: 1 }),
                end: endOfWeek(currentDate, { weekStartsOn: 1 }),
            };
        }
        if (view === "list") {
            const s = new Date(currentDate); s.setMonth(s.getMonth() - 1);
            const e = new Date(currentDate); e.setMonth(e.getMonth() + 3);
            return { start: s, end: e };
        }
        return {
            start: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 }),
            end: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 }),
        };
    }, [view, currentDate]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/activities", {
                params: {
                    start: range.start.toISOString(),
                    end: range.end.toISOString(),
                },
            });
            setActivities(data);
        } finally {
            setLoading(false);
        }
    }, [range.start, range.end]);

    useEffect(() => { load(); }, [load]);

    const goPrev = () => setCurrentDate(view === "week" ? addWeeks(currentDate, -1) : addMonths(currentDate, -1));
    const goNext = () => setCurrentDate(view === "week" ? addWeeks(currentDate, 1) : addMonths(currentDate, 1));
    const goToday = () => setCurrentDate(new Date());

    const openCreate = (date) => {
        setEditActivity(null);
        setInitialDate(date || new Date());
        setDialogOpen(true);
    };

    const openActivity = (a) => {
        setDrawerId(a.id);
        setDrawerOpen(true);
    };

    const onEdit = (a) => {
        setDrawerOpen(false);
        setEditActivity(a);
        setDialogOpen(true);
    };

    const titleLabel = view === "week"
        ? `${format(range.start, "MMM d")} — ${format(range.end, "MMM d, yyyy")}`
        : format(currentDate, "MMMM yyyy");

    return (
        <div className="min-h-screen flex flex-col">
            <Header />
            <main className="flex-1 max-w-[1400px] w-full mx-auto px-6 lg:px-10 py-8 lg:py-12">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
                    <div>
                        <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2">Shared calendar</p>
                        <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tight text-[#111110]" data-testid="calendar-title">
                            {titleLabel}
                        </h1>
                    </div>
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="flex border border-[#E5E5E3]" data-testid="view-toggle">
                            {VIEWS.map(v => (
                                <button
                                    key={v}
                                    onClick={() => setView(v)}
                                    data-testid={`view-${v}`}
                                    className={`px-4 h-10 text-xs tracking-[0.15em] uppercase font-medium transition-colors ${
                                        view === v ? "bg-[#111110] text-white" : "bg-white text-[#333333] hover:bg-[#F0F0EE]"
                                    }`}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                        <div className="flex border border-[#E5E5E3]" data-testid="nav-controls">
                            <button onClick={goPrev} className="w-10 h-10 bg-white hover:bg-[#F0F0EE] text-[#111110] flex items-center justify-center" data-testid="nav-prev">
                                <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <button onClick={goToday} className="px-4 h-10 bg-white hover:bg-[#F0F0EE] text-[#111110] text-xs tracking-[0.15em] uppercase font-medium border-l border-r border-[#E5E5E3]" data-testid="nav-today">
                                Today
                            </button>
                            <button onClick={goNext} className="w-10 h-10 bg-white hover:bg-[#F0F0EE] text-[#111110] flex items-center justify-center" data-testid="nav-next">
                                <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </div>
                        <Button
                            variant="outline"
                            onClick={() => setImportOpen(true)}
                            data-testid="import-events-btn"
                            className="rounded-none h-10 border-[#E5E5E3] text-[#111110] hover:bg-[#F0F0EE] px-5 font-medium text-xs tracking-[0.15em] uppercase"
                        >
                            <Upload className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Import
                        </Button>
                        <Button
                            onClick={() => openCreate(currentDate)}
                            data-testid="create-activity-btn"
                            className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-5 font-medium"
                        >
                            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> New activity
                        </Button>
                    </div>
                </div>

                {loading ? (
                    <div className="border border-[#E5E5E3] bg-white p-16 text-center text-[#888885] text-sm tracking-[0.15em] uppercase" data-testid="calendar-loading">
                        Loading…
                    </div>
                ) : view === "month" ? (
                    <CalendarMonth currentDate={currentDate} activities={activities}
                        onDayClick={(d) => openCreate(d)}
                        onActivityClick={openActivity} />
                ) : view === "week" ? (
                    <CalendarWeek currentDate={currentDate} activities={activities}
                        onDayClick={(d) => openCreate(d)}
                        onActivityClick={openActivity} />
                ) : (
                    <CalendarList activities={activities} onActivityClick={openActivity} />
                )}
            </main>

            <ActivityDrawer
                activityId={drawerId}
                open={drawerOpen}
                onOpenChange={setDrawerOpen}
                onChanged={load}
                onEdit={onEdit}
            />

            <CreateActivityDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                initialDate={initialDate}
                activity={editActivity}
                onSaved={load}
            />

            <EventImportDialog
                open={importOpen}
                onOpenChange={setImportOpen}
                onImported={load}
            />
        </div>
    );
}
